/**
 * SharePoint folder picker for Obsidian Import.
 *
 * Modal dialog: pick a site → pick a drive → navigate into a folder →
 * click "Use this folder" to return an ImportSource. The host
 * (ObsidianImportWindow) feeds that into the existing
 * `walkImportSource` pipeline so the SP path lands on the same
 * progress + summary screens as FSAP / drag-drop / zip.
 *
 * Building blocks reused: `lib/vault/importers/sharepoint.ts`
 * (`buildSharePointSource`), `lib/graph/sharepoint.ts` list/get
 * helpers, `usePortalSites` + `usePortalDrives` hooks.
 *
 * Demo mode: SP picker shows a friendly "Not available in demo —
 * connect Microsoft account first" message (Graph plumbing stays
 * live-only).
 *
 * Conventions:
 *   - Tailwind only (feedback_use_tailwind)
 *   - Black text on light bg (feedback_no_light_grey_text)
 *   - data-testid from testIds.ts (feedback_data_testid)
 *   - withRetry wraps every Graph call inside lib/graph/sharepoint.ts
 *     (memory feedback_graph_retry_policy) so we get it for free
 */

import { useCallback, useEffect, useState } from 'react';

import type { Drive, Site } from '@microsoft/microsoft-graph-types';

import { getContent, listChildren } from '../../lib/graph/sharepoint';
import {
  usePortalDrives,
  usePortalSites,
} from '../../lib/tenant/usePortalSharePoint';
import { testIds } from '../../lib/testIds';
import {
  buildSharePointSource,
  type ImportSource,
  type SharePointDriveItemLike,
  type SharePointReader,
} from '../../lib/vault/importers';

export interface SharePointFolderPickerProps {
  open: boolean;
  onCancel: () => void;
  /** Called when the user picks a folder; returns to the import wizard. */
  onPick: (source: ImportSource) => void;
}

interface BreadcrumbEntry {
  id: string | 'root';
  name: string;
}

/**
 * Build a SharePointReader against the live Graph helpers. Each call
 * already wraps in withRetry(READ_POLICY) per memory
 * feedback_graph_retry_policy.md, so we don't add a second layer here.
 */
function createLiveReader(): SharePointReader {
  return {
    listChildren: async (driveId, itemId) => {
      const children = await listChildren(driveId, itemId as string);
      // The Graph SDK's DriveItem shape is wider than our minimal
      // SharePointDriveItemLike; cast through unknown for the bits we
      // don't use.
      return children as unknown as SharePointDriveItemLike[];
    },
    getTextContent: async (driveId, itemId) => {
      const buf = await getContent(driveId, itemId);
      return new TextDecoder().decode(buf);
    },
    getBytesContent: async (driveId, itemId) => {
      const buf = await getContent(driveId, itemId);
      return new Uint8Array(buf);
    },
  };
}

export function SharePointFolderPicker({
  open,
  onCancel,
  onPick,
}: SharePointFolderPickerProps) {
  const sitesState = usePortalSites();
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const drivesState = usePortalDrives(selectedSite?.id ?? null);
  const [selectedDrive, setSelectedDrive] = useState<Drive | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbEntry[]>([
    { id: 'root', name: '/' },
  ]);
  const [folderState, setFolderState] = useState<
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'ok'; folders: SharePointDriveItemLike[] }
    | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  // Reset all selections when the dialog is dismissed.
  useEffect(() => {
    if (!open) {
      setSelectedSite(null);
      setSelectedDrive(null);
      setBreadcrumbs([{ id: 'root', name: '/' }]);
      setFolderState({ kind: 'idle' });
    }
  }, [open]);

  // Re-list children whenever the drive or breadcrumb tip changes.
  useEffect(() => {
    if (!open || !selectedDrive || !selectedDrive.id) return;
    const tip = breadcrumbs[breadcrumbs.length - 1]!;
    const driveId = selectedDrive.id;
    let cancelled = false;
    setFolderState({ kind: 'loading' });
    listChildren(driveId, tip.id as string)
      .then((children) => {
        if (cancelled) return;
        const folders = (children as unknown as SharePointDriveItemLike[]).filter(
          (c) => c.folder,
        );
        setFolderState({ kind: 'ok', folders });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setFolderState({
          kind: 'error',
          message:
            err instanceof Error ? err.message : 'Failed to list folder.',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [open, selectedDrive, breadcrumbs]);

  const enterFolder = useCallback((folder: SharePointDriveItemLike) => {
    setBreadcrumbs((prev) => [...prev, { id: folder.id, name: folder.name }]);
  }, []);

  const jumpTo = useCallback((idx: number) => {
    setBreadcrumbs((prev) => prev.slice(0, idx + 1));
  }, []);

  const handlePick = useCallback(() => {
    if (!selectedSite?.id || !selectedDrive?.id) return;
    const tip = breadcrumbs[breadcrumbs.length - 1]!;
    const label = `${selectedSite.displayName}/${selectedDrive.name}${
      tip.id === 'root' ? '' : breadcrumbs.slice(1).map((b) => '/' + b.name).join('')
    }`;
    const source = buildSharePointSource(createLiveReader(), {
      siteId: selectedSite.id,
      driveId: selectedDrive.id,
      ...(tip.id === 'root' ? {} : { folderItemId: tip.id }),
      label,
    });
    onPick(source);
  }, [breadcrumbs, onPick, selectedDrive, selectedSite]);

  if (!open) return null;

  return (
    <div
      data-testid={testIds.obsidianImport.sharePointPickerOverlay}
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40"
      onClick={onCancel}
    >
      <div
        data-testid={testIds.obsidianImport.sharePointPickerDialog}
        onClick={(e) => e.stopPropagation()}
        className="w-[560px] max-h-[80vh] flex flex-col bg-[#ece9d8] border border-[#7f9db9] shadow-[3px_3px_0_0_#000] text-black"
        style={{ fontFamily: "var(--xp-font-body)", fontSize: 12 }}
      >
        <div className="bg-gradient-to-r from-[var(--xp-selection)] to-[#1a4ab8] text-white px-2 py-1 font-bold flex items-center justify-between">
          <span>Pick a SharePoint folder</span>
          <button
            type="button"
            aria-label="Close"
            data-testid={testIds.obsidianImport.sharePointPickerClose}
            onClick={onCancel}
            className="px-2 bg-[#ece9d8] text-black border-t border-l border-white border-b border-r border-black hover:bg-[#d0d0d0]"
          >
            ✕
          </button>
        </div>

        <div className="p-3 overflow-y-auto flex-1">
          {/* Step 1 — site picker */}
          {!selectedSite && (
            <SitePicker state={sitesState} onPick={setSelectedSite} />
          )}

          {/* Step 2 — drive picker */}
          {selectedSite && !selectedDrive && (
            <DrivePicker
              state={drivesState}
              site={selectedSite}
              onBack={() => setSelectedSite(null)}
              onPick={setSelectedDrive}
            />
          )}

          {/* Step 3 — folder navigator */}
          {selectedSite && selectedDrive && (
            <FolderNavigator
              site={selectedSite}
              drive={selectedDrive}
              breadcrumbs={breadcrumbs}
              folderState={folderState}
              onBack={() => setSelectedDrive(null)}
              onEnter={enterFolder}
              onJumpTo={jumpTo}
            />
          )}
        </div>

        {selectedSite && selectedDrive && (
          <div className="p-2 border-t border-gray-700 flex justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="px-3 py-1 border border-black bg-gray-200 hover:bg-gray-300"
              data-testid={testIds.obsidianImport.sharePointPickerCancel}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handlePick}
              data-testid={testIds.obsidianImport.sharePointPickerConfirm}
              className="px-3 py-1 border border-black bg-blue-700 text-white hover:bg-blue-800"
            >
              Use this folder
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────

interface SitePickerProps {
  state: ReturnType<typeof usePortalSites>;
  onPick: (site: Site) => void;
}

function SitePicker({ state, onPick }: SitePickerProps) {
  if (state.kind === 'idle') {
    return (
      <div className="text-black">
        Sign in to Microsoft 365 first (Settings → Microsoft 365 → Connect).
      </div>
    );
  }
  if (state.kind === 'loading') return <div>Loading sites…</div>;
  if (state.kind === 'error') {
    return <div className="text-red-800">Failed: {state.message}</div>;
  }
  if (state.value.length === 0) {
    return (
      <div>
        No SharePoint sites accessible. Your portal may be granted
        access to a different set of sites than your account can see.
      </div>
    );
  }
  return (
    <div>
      <h2 className="font-semibold mb-2">Pick a site</h2>
      <ul className="space-y-1">
        {state.value
          .filter((s): s is Site & { id: string } => typeof s.id === 'string')
          .map((s) => (
            <li key={s.id}>
              <button
                type="button"
                data-testid={testIds.obsidianImport.sharePointPickerSiteRow(s.id)}
                className="w-full text-left px-2 py-1 border border-gray-700 bg-white hover:bg-blue-50"
                onClick={() => onPick(s)}
              >
                <div className="font-semibold">{s.displayName}</div>
                {s.webUrl && (
                  <div className="text-xs text-gray-800 truncate">{s.webUrl}</div>
                )}
              </button>
            </li>
          ))}
      </ul>
    </div>
  );
}

interface DrivePickerProps {
  state: ReturnType<typeof usePortalDrives>;
  site: Site;
  onBack: () => void;
  onPick: (drive: Drive) => void;
}

function DrivePicker({ state, site, onBack, onPick }: DrivePickerProps) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2 text-sm">
        <button
          type="button"
          onClick={onBack}
          className="underline text-blue-800"
        >
          ← Sites
        </button>
        <span className="text-black">/ {site.displayName}</span>
      </div>
      {state.kind === 'loading' && <div>Loading drives…</div>}
      {state.kind === 'error' && (
        <div className="text-red-800">Failed: {state.message}</div>
      )}
      {state.kind === 'ok' && state.value.length === 0 && (
        <div>This site has no drives.</div>
      )}
      {state.kind === 'ok' && state.value.length > 0 && (
        <>
          <h2 className="font-semibold mb-2">Pick a drive</h2>
          <ul className="space-y-1">
            {state.value
              .filter((d): d is Drive & { id: string } => typeof d.id === 'string')
              .map((d) => (
                <li key={d.id}>
                  <button
                    type="button"
                    data-testid={testIds.obsidianImport.sharePointPickerDriveRow(d.id)}
                    className="w-full text-left px-2 py-1 border border-gray-700 bg-white hover:bg-blue-50"
                    onClick={() => onPick(d)}
                  >
                    {d.name}
                  </button>
                </li>
              ))}
          </ul>
        </>
      )}
    </div>
  );
}

interface FolderNavigatorProps {
  site: Site;
  drive: Drive;
  breadcrumbs: BreadcrumbEntry[];
  folderState:
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'ok'; folders: SharePointDriveItemLike[] }
    | { kind: 'error'; message: string };
  onBack: () => void;
  onEnter: (folder: SharePointDriveItemLike) => void;
  onJumpTo: (idx: number) => void;
}

function FolderNavigator({
  site,
  drive,
  breadcrumbs,
  folderState,
  onBack,
  onEnter,
  onJumpTo,
}: FolderNavigatorProps) {
  return (
    <div>
      <div className="flex flex-wrap items-center gap-1 mb-2 text-xs">
        <button
          type="button"
          onClick={onBack}
          className="underline text-blue-800"
        >
          ← Drives
        </button>
        <span className="text-black">/ {site.displayName} / {drive.name}</span>
      </div>
      <div
        data-testid={testIds.obsidianImport.sharePointPickerBreadcrumbs}
        className="mb-2 text-xs"
      >
        {breadcrumbs.map((b, i) => (
          <span key={`${b.id}-${i}`}>
            {i > 0 && <span className="text-black"> / </span>}
            <button
              type="button"
              onClick={() => onJumpTo(i)}
              className="underline text-blue-800"
            >
              {b.name === '/' ? '(root)' : b.name}
            </button>
          </span>
        ))}
      </div>
      {folderState.kind === 'loading' && <div>Loading folders…</div>}
      {folderState.kind === 'error' && (
        <div className="text-red-800">Failed: {folderState.message}</div>
      )}
      {folderState.kind === 'ok' && folderState.folders.length === 0 && (
        <div className="text-black">No subfolders here. Click "Use this folder" to import everything in the current location (files too).</div>
      )}
      {folderState.kind === 'ok' && folderState.folders.length > 0 && (
        <ul className="space-y-1 max-h-[280px] overflow-y-auto">
          {folderState.folders.map((f) => (
            <li key={f.id}>
              <button
                type="button"
                data-testid={testIds.obsidianImport.sharePointPickerFolderRow(f.id)}
                onClick={() => onEnter(f)}
                className="w-full text-left px-2 py-1 border border-gray-700 bg-white hover:bg-blue-50"
              >
                📁 {f.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
